import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";
import type * as fhirTypes from "fhir/r4";

interface SubmitOrderResponse {
  fhirResources: fhirTypes.Resource[];
  workflowId: string;
}

export const useSubmitOrder = ({
  patientId,
  encounterId,
  requestGroupId,
  markOnHold,
  serviceRequestId,
}: {
  patientId: string;
  encounterId: string;
  requestGroupId: string;
  markOnHold: boolean;
  serviceRequestId?: string;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<SubmitOrderResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.Labs.SUBMIT_ORDER({
        patientId,
        encounterId,
        requestGroupId,
      }),
      mutationFn: () => {
        return markOnHold
          ? axiosInstance.post(`/labs/order/${requestGroupId}/mark-onhold`)
          : axiosInstance.post(`/labs/order/${requestGroupId}/submit`);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Labs.DEFAULT({
            patientId,
            encounterId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Labs.DEFAULT_V2({
            patientId,
            encounterId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Labs.DEFAULT(patientId),
        });

        if (serviceRequestId) {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.MedicalRecords.Labs.RELATED_RESOURCES(
              patientId,
              serviceRequestId,
            ),
          });
        }
      },
    },
  });
};
